import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {StyledAcknowledgeBar} from '../specificationViewerPage.style';
import {postAcknowledge} from '../../../services/specificationViewer.service';
import {AcknowledgmentStatus} from '../../../models/specificationViewer';

type AcknowledgeBarProps = {
    specificationId: string;
    reportId: string;
    scrolledToBottom: boolean;
    acknowledgment: AcknowledgmentStatus | null;
    onAcknowledged: (status: AcknowledgmentStatus) => void;
    returnTo?: string;
}

const AcknowledgeBar: React.FC<AcknowledgeBarProps> = ({specificationId, reportId, scrolledToBottom, acknowledgment, onAcknowledged, returnTo}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const [submitting, setSubmitting] = useState(false);

    const handleAcknowledge = async () => {
        if (submitting || !scrolledToBottom || acknowledgment?.acknowledged) return;

        setSubmitting(true);
        try {
            await postAcknowledge(specificationId, reportId);
            onAcknowledged({
                acknowledged: true,
                signatureRequired: true,
                specificationId: parseInt(specificationId),
                acknowledgedAt: new Date().toISOString()
            });
            if (returnTo) {
                history.replace(returnTo);
            }
        } catch (e) {
            console.error('Acknowledge failed', e);
        } finally {
            setSubmitting(false);
        }
    };

    const getButtonClass = () => {
        if (acknowledgment?.acknowledged) return 'acknowledge-btn acknowledged';
        if (!scrolledToBottom) return 'acknowledge-btn disabled';
        return 'acknowledge-btn ready';
    };

    const getButtonText = () => {
        if (acknowledgment?.acknowledged) {
            return t('specificationViewer.acknowledgedOn', {date: acknowledgment.acknowledgedAt ? new Date(acknowledgment.acknowledgedAt).toLocaleDateString() : ''});
        }
        if (!scrolledToBottom) {
            return t('specificationViewer.scrollToBottom');
        }
        return t('specificationViewer.confirmSpecification');
    };

    return (
        <StyledAcknowledgeBar>
            {scrolledToBottom && !acknowledgment?.acknowledged && (
                <div className="declaration-text">
                    {t('specificationViewer.declarationText', {number: specificationId})}
                </div>
            )}
            <button
                className={getButtonClass()}
                onClick={handleAcknowledge}
                disabled={submitting || !scrolledToBottom || !!acknowledgment?.acknowledged}
            >
                {submitting && <div className="spinner"/>}
                {getButtonText()}
            </button>
        </StyledAcknowledgeBar>
    );
};

export default AcknowledgeBar;
